import { useCallback, useState } from 'react';
import { downloadFileFromUrl } from '../utils/downloadHelpers';
import api from '../../../api';

/**
 * Hook for handling single file download with progress tracking
 * @returns {Object} Download function and state
 */
export const useSingleFileDownload = () => {
  const [downloadState, setDownloadState] = useState({
    isDownloading: false,
    current: 0,
    total: 1,
    stage: 'idle' // 'idle' | 'preparing' | 'downloading' | 'complete'
  });

  const downloadFile = useCallback(async (filename, getUrlFn) => {
    setDownloadState({ isDownloading: true, current: 0, total: 1, stage: 'preparing' });

    try {
      // Get download URL (either from provided function or API)
      let downloadUrl;
      if (getUrlFn) {
        downloadUrl = await getUrlFn(filename);
      } else {
        const token = localStorage.getItem('weddingToken');
        const response = await api.post('/download-urls', {
          fileNames: [filename]
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const { urls } = response.data;
        if (!urls || urls.length === 0 || !urls[0].url) {
          throw new Error(urls[0]?.error || 'Failed to generate download URL');
        }
        downloadUrl = urls[0].url;
      }

      setDownloadState(prev => ({ ...prev, stage: 'downloading', current: 0 }));
      
      // Download the file
      await downloadFileFromUrl(downloadUrl, filename);
      
      setDownloadState(prev => ({ ...prev, stage: 'complete', current: 1 }));
      
      // Auto-hide after a brief delay
      setTimeout(() => {
        setDownloadState({ isDownloading: false, current: 0, total: 1, stage: 'idle' });
      }, 1000);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadState({ isDownloading: false, current: 0, total: 1, stage: 'idle' });
      throw error;
    }
  }, []);

  return { downloadFile, downloadState };
};
